import React, { useCallback, useEffect, useMemo, useState } from "react"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import type { IPagination } from "@common-shared/types/general.types"
import type { IOneVerifyPayerMapping } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import { PAYER_MAPPINGS_CONFIG } from "../../../../config/payerMappings.config"
import { POLICIES_CONFIG } from "../../../../config/policies.config"
import { RuntimeHelper } from "../../../../helpers/runtime.helper"
import { UtilHelper } from "../../../../helpers/util.helper"
import { useHasPermissions } from "../../../../hooks/hasPermissions.hook"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { payerMappingsSelector } from "../../../../store/selectors/payerMappings.selector"
import { runtimeSelector } from "../../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../../store/selectors/selectedPracticeRole.selector"
import {
    payerMappingsDelete,
    payerMappingsGetList,
} from "../../../../store/thunks/payerMappings.thunks"
import { PoliciesListPaginationComponent } from "../../../policies/policiesList/policiesListPagination.component"
import { UpsertPayerMappingPopupComponent } from "../../../popups/upsertPayerMappingPopup.component"
import { PayerMappingsTableHeaderComponent } from "./payerMappingsTableHeader.component"
import { PayerMappingsTableRowComponent } from "./payerMappingsTableRow.component"

import editIcon from "@front-shared/assets/images/icons/edit-dark.svg"
import removeIcon from "@front-shared/assets/images/icons/trash-red.svg"

export const PayerMappingsTableComponent: React.FunctionComponent<
    IPayerMappingsTableComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const payerMappings = useAppSelector(payerMappingsSelector)

    const canManagePracticeConfigurations = useHasPermissions(
        OneVerifyPermission.managePracticeConfigurations,
        undefined,
        false,
    )

    const isLoadingList = useMemo<boolean>(() => {
        return RuntimeHelper.isPayerMappingsGetListLoading()
    }, [runtimeState.isLoading])

    const [selectedPaginationOption, setSelectedPaginationOption] =
        useState<ISelectRenderedOption>(
            BrowserStorageHelper.get<ISelectRenderedOption>(
                PAYER_MAPPINGS_CONFIG.listPerPageStorageKey,
                undefined,
                BrowserStorageType.sessionStorage,
            ) ?? POLICIES_CONFIG.availablePaginationOptions[0],
        )

    const [mappingToEdit, setMappingToEdit] = useState<
        IOneVerifyPayerMapping | undefined
    >(undefined)

    const [isUpsertingMapping, setIsUpsertingMapping] = useState<boolean>(false)

    const getItems = useCallback(
        (pagination: IPagination, ignorePreloader = false) => {
            if (!selectedPracticeRole) {
                return
            }

            return dispatch(
                payerMappingsGetList({
                    practice: selectedPracticeRole.practice,
                    payload: {
                        start: pagination.start,
                        count: pagination.count,
                        externalPayerId: props.externalPayerIdSearch,
                    },
                    ignorePreloader,
                }),
            )
        },
        [dispatch, selectedPracticeRole, props.externalPayerIdSearch],
    )

    // Removing a row shifts every row after it, so the page on screen is reloaded
    // instead of patched locally. start is the offset of the next page, so the
    // current one begins a page earlier, clamped to a page that still exists once
    // the row is gone - which only bites when the last page held a single row.
    const refetchCurrentPage = () => {
        const { start, count, total } = payerMappings.itemsPagination

        const lastPageStart =
            count > 0
                ? Math.max(0, Math.ceil((total - 1) / count) - 1) * count
                : 0

        return getItems(
            {
                ...payerMappings.itemsPagination,
                start: Math.min(Math.max(0, start - count), lastPageStart),
            },
            true,
        )
    }

    const onEditMapping = (item: IOneVerifyPayerMapping) => {
        if (!canManagePracticeConfigurations) {
            return
        }

        setMappingToEdit(item)
        setIsUpsertingMapping(true)
    }

    const onUpsertingMappingDone = () => {
        setIsUpsertingMapping(false)
        setMappingToEdit(undefined)
    }

    const onRemoveMapping = (item: IOneVerifyPayerMapping) => {
        if (!selectedPracticeRole || !canManagePracticeConfigurations) {
            return
        }

        ConfirmationHelper.enableConfirmation(
            () => {
                dispatch(
                    payerMappingsDelete({
                        payload: item.id,
                        practice: selectedPracticeRole.practice,
                        onSuccess: refetchCurrentPage,
                    }),
                )
            },
            undefined,
            `Payer mapping "${item.externalName || item.externalPayerId}" will be permanently removed`,
        )
    }

    useEffect(() => {
        BrowserStorageHelper.set(
            PAYER_MAPPINGS_CONFIG.listPerPageStorageKey,
            selectedPaginationOption,
            BrowserStorageType.sessionStorage,
        )
    }, [selectedPaginationOption])

    // Load fresh list of payers on selectedPracticeRole or page size change
    useEffect(() => {
        const promise = getItems(
            UtilHelper.getPagination(selectedPaginationOption.value),
        )

        return () => {
            try {
                promise?.abort()
            } catch (_e) {}
        }
    }, [getItems, selectedPaginationOption.value])

    return (
        <div
            data-produktly="payer-mappings-table"
            className="max-w-[840px] mx-auto relative"
        >
            <PayerMappingsTableHeaderComponent
                withActionsColumn={canManagePracticeConfigurations ?? false}
            />

            {isLoadingList ? (
                <SpinnerElement containerClassName="mt-[24px] text-center" />
            ) : payerMappings.items.length === 0 ? (
                <PrimaryText className="mt-[24px] text-center opacity-50">
                    No payer mappings found
                </PrimaryText>
            ) : (
                payerMappings.items.map((item, index) => (
                    <PayerMappingsTableRowComponent
                        key={index}
                        externalName={item.externalName}
                        externalPayerId={item.externalPayerId}
                        payerId={item.payerId}
                        actions={
                            canManagePracticeConfigurations ? (
                                <div
                                    className="
                                        w-[56px] flex items-center
                                        md:absolute md:top-[16px] md:right-[16px]
                                    "
                                >
                                    <img
                                        className="mr-[24px] cursor-pointer"
                                        src={removeIcon}
                                        alt="Remove"
                                        onClick={() => {
                                            onRemoveMapping(item)
                                        }}
                                    />

                                    <img
                                        className="cursor-pointer"
                                        src={editIcon}
                                        alt="Edit"
                                        onClick={() => {
                                            onEditMapping(item)
                                        }}
                                    />
                                </div>
                            ) : undefined
                        }
                    />
                ))
            )}

            {!isLoadingList && payerMappings.itemsPagination.total > 0 ? (
                <PoliciesListPaginationComponent
                    alignLeft={true}
                    getNewItems={getItems}
                    nextPagination={payerMappings.itemsPagination}
                    selectedPaginationOption={selectedPaginationOption}
                    setSelectedPaginationOption={setSelectedPaginationOption}
                />
            ) : null}

            {canManagePracticeConfigurations && (
                <UpsertPayerMappingPopupComponent
                    isActive={isUpsertingMapping}
                    // Close and done are the same here, because state changes are handled via redux
                    onDone={onUpsertingMappingDone}
                    onClose={onUpsertingMappingDone}
                    existingMapping={mappingToEdit}
                />
            )}
        </div>
    )
}

interface IPayerMappingsTableComponentProps {
    externalPayerIdSearch?: string
}
